import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useApexTheme, mergeThemeOptions } from '../utils/apexTheme';

const PriceWithSignalsChart = ({ priceSeries, trades, trendLines, height = 450 }) => {
  const { baseThemeOptions, colors, isDark } = useApexTheme();

  const { options, series } = useMemo(() => {
    if (!priceSeries || priceSeries.length === 0) return { options: null, series: [] };

    // Sort price series by timestamp
    const sortedPrices = [...priceSeries].sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    const sortedTimes = sortedPrices.map(item => new Date(item.timestamp).getTime());

    // Create a map of timestamp to index for signal placement
    const timestampToIndex = new Map();
    sortedPrices.forEach((item, index) => {
      timestampToIndex.set(new Date(item.timestamp).getTime(), index);
    });

    const findNearestIndex = (targetTime) => {
      if (!sortedTimes.length) return undefined;
      if (targetTime <= sortedTimes[0]) return 0;
      if (targetTime >= sortedTimes[sortedTimes.length - 1]) return sortedTimes.length - 1;

      let lo = 0;
      let hi = sortedTimes.length - 1;

      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const midVal = sortedTimes[mid];
        if (midVal === targetTime) return mid;
        if (midVal < targetTime) lo = mid + 1;
        else hi = mid - 1;
      }

      const right = lo;
      const left = lo - 1;
      if (left < 0) return right;
      if (right >= sortedTimes.length) return left;
      return (targetTime - sortedTimes[left]) <= (sortedTimes[right] - targetTime) ? left : right;
    };

    // Format date for x-axis labels (short format)
    const formatDateShort = (timestamp) => {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    };

    // Format full datetime for tooltip
    const formatDateFull = (timestamp) => {
      const date = new Date(timestamp);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    // Prepare candlestick data with index-based x values
    const candleData = sortedPrices.map((item, index) => ({
      x: index,
      y: [item.open, item.high, item.low, item.close]
    }));

    const trendLineSeries = (trendLines || []).map((line, index) => {
      const x1 = typeof line?.x1 === 'string' ? Number(line.x1) : line?.x1;
      const x2 = typeof line?.x2 === 'string' ? Number(line.x2) : line?.x2;

      const i1 = Number.isFinite(x1) ? findNearestIndex(x1) : undefined;
      const i2 = Number.isFinite(x2) ? findNearestIndex(x2) : undefined;

      if (i1 === undefined || i2 === undefined) {
        return null;
      }

      return {
        name: line?.name || `Trend Line ${index + 1}`,
        type: 'line',
        data: [
          { x: i1, y: line?.y1 },
          { x: i2, y: line?.y2 }
        ]
      };
    }).filter(Boolean);

    // Create category labels for x-axis (short format)
    const categories = sortedPrices.map(item => formatDateShort(item.timestamp));

    // Store full timestamps for tooltip
    const fullTimestamps = sortedPrices.map(item => formatDateFull(item.timestamp));

    // Prepare buy/sell annotations from trades
    const annotations = [];

    if (trades && trades.length > 0) {
      trades.forEach((trade) => {
        const entryTime = new Date(trade.entry_time).getTime();
        const exitTime = new Date(trade.exit_time).getTime();
        const isBuy = trade.side === 'BUY';

        // Use nearest index matching to handle downsampled (daily) candles
        const entryIndex = findNearestIndex(entryTime);
        const exitIndex = findNearestIndex(exitTime);

        // Entry annotation
        if (entryIndex !== undefined) {
          annotations.push({
            x: entryIndex,
            y: trade.entry_price,
            marker: {
              size: 6,
              fillColor: isBuy ? (colors.success || '#4caf50') : (colors.error || '#f44336'),
              strokeColor: '#fff',
              strokeWidth: 2
            },
            label: {
              borderColor: isBuy ? (colors.success || '#4caf50') : (colors.error || '#f44336'),
              style: {
                color: '#fff',
                background: isBuy ? (colors.success || '#4caf50') : (colors.error || '#f44336'),
                fontSize: '10px'
              },
              text: isBuy ? 'B' : 'S',
              offsetY: -10
            }
          });
        }

        // Exit annotation
        if (exitIndex !== undefined) {
          annotations.push({
            x: exitIndex,
            y: trade.exit_price,
            marker: {
              size: 6,
              fillColor: isBuy ? (colors.error || '#f44336') : (colors.success || '#4caf50'),
              strokeColor: '#fff',
              strokeWidth: 2
            },
            label: {
              borderColor: isBuy ? (colors.error || '#f44336') : (colors.success || '#4caf50'),
              style: {
                color: '#fff',
                background: isBuy ? (colors.error || '#f44336') : (colors.success || '#4caf50'),
                fontSize: '10px'
              },
              text: isBuy ? 'S' : 'B',
              offsetY: -10
            }
          });
        }
      });
    }

    const componentOptions = {
      chart: {
        type: 'line',
        height,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true
          }
        },
        zoom: {
          enabled: true
        }
      },
      xaxis: {
        type: 'category',
        categories: categories,
        labels: {
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          showDuplicates: false,
          trim: true,
          style: {
            fontSize: '10px'
          }
        },
        tickAmount: Math.min(20, categories.length),
        tickPlacement: 'on'
      },
      yaxis: {
        title: {
          text: 'Price (₹)'
        },
        labels: {
          formatter: (value) => '₹' + value.toFixed(2)
        },
        tooltip: {
          enabled: true
        }
      },
      plotOptions: {
        candlestick: {
          colors: {
            upward: colors.success || '#4caf50',
            downward: colors.error || '#f44336'
          },
          wick: {
            useFillColor: true
          }
        }
      },
      stroke: {
        curve: 'straight',
        width: [1].concat(new Array(trendLineSeries.length).fill(2))
      },
      tooltip: {
        enabled: true,
        custom: ({ seriesIndex, dataPointIndex, w }) => {
          if (!w.globals.seriesCandleO?.[seriesIndex]) {
            return '';
          }

          const o = w.globals.seriesCandleO[seriesIndex][dataPointIndex];
          const h = w.globals.seriesCandleH[seriesIndex][dataPointIndex];
          const l = w.globals.seriesCandleL[seriesIndex][dataPointIndex];
          const c = w.globals.seriesCandleC[seriesIndex][dataPointIndex];
          const dateLabel = fullTimestamps[dataPointIndex] || '';
          return `<div class="p-2 ${isDark ? 'bg-base-300 text-base-content' : 'bg-base-100 text-base-content'} border border-base-300 rounded shadow">
            <div class="font-bold">${dateLabel}</div>
            <div>Open: ₹${o?.toFixed(2) || 'N/A'}</div>
            <div>High: ₹${h?.toFixed(2) || 'N/A'}</div>
            <div>Low: ₹${l?.toFixed(2) || 'N/A'}</div>
            <div>Close: ₹${c?.toFixed(2) || 'N/A'}</div>
          </div>`;
        }
      },
      annotations: {
        points: annotations
      }
    };

    return {
      options: mergeThemeOptions(baseThemeOptions, componentOptions),
      series: [
        {
          name: 'Price',
          type: 'candlestick',
          data: candleData
        },
        ...trendLineSeries
      ]
    };
  }, [priceSeries, trades, trendLines, height, baseThemeOptions, colors, isDark]);

  if (!options) {
    return (
      <div className="flex items-center justify-center text-base-content/60" style={{ height }}>
        No price data available
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-bold mb-2">Price Chart with Buy/Sell Signals</h4>
      <div className="flex gap-4 mb-2 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-success"></span> Buy Signal
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-error"></span> Sell Signal
        </span>
      </div>
      <Chart
        options={options}
        series={series}
        type="line"
        height={height}
      />
    </div>
  );
};

export default PriceWithSignalsChart;
